import React from "react";
import { defineMessages } from "react-intl";
import { graphql } from "react-apollo";
import { Section } from "react-native-responsive-layout";
import { withTheme } from "styled-components/native";
import {
  string as yupString,
  object as yupObject,
  array as yupArray
} from "yup";
import { Field, FormSpy } from "react-final-form";
import {
  Button,
  Container,
  DeletableItem,
  FormCell,
  FormCheckbox,
  FormTextField,
  FormEmailField,
  FormSelectValueField,
  FormToManyField,
  FormUploadImageField,
  RoundedIcon
} from "cci-component";
import {
  findBusinessByIdQuery,
  saveBusinessMutation,
  deleteBusinessMutation,
  getSessionInformation
} from "cci-data";
import { hasPermissions, SecuredComponent } from "cci-logic";
import MutableInspect from "../../inspect/MutableInspect";
import { InspectSubtitle } from "../../layout/Titles";
import FormGeneralStatusField from "../../form/FormGeneralStatusField";

const messages = defineMessages({
  business: {
    id: "business",
    defaultMessage: "Business"
  },
  companyName: {
    id: "companyName",
    defaultMessage: "Team Name"
  },
  firstName: {
    id: "firstName",
    defaultMessage: "First Name"
  },
  lastName: {
    id: "lastName",
    defaultMessage: "Last Name"
  },
  emails: {
    id: "emails",
    defaultMessage: "E-mails"
  },
  type: {
    id: "type",
    defaultMessage: "Type"
  },
  addresses: {
    id: "addresses",
    defaultMessage: "Addresses"
  },
  phones: {
    id: "phones",
    defaultMessage: "Phones"
  },
  line1: {
    id: "line1",
    defaultMessage: "Address"
  },
  city: {
    id: "city",
    defaultMessage: "City"
  },
  state: {
    id: "state",
    defaultMessage: "State"
  },
  zipCode: {
    id: "zipCode",
    defaultMessage: "Zip Code"
  },
  number: {
    id: "number",
    defaultMessage: "Number"
  },
  confirmDeleteItem: {
    id: "confirmDeleteItem",
    defaultMessage: "Are you sure you want to delete this item?"
  },
  medicalPractice: {
    id: "medicalPractice",
    defaultMessage: "Medical Practice"
  },
  mfaEnabled: {
    id: "mfaEnabled",
    defaultMessage: "2FA Enabled"
  },
  securityAgency: { defaultMessage: "Security Agency", id: "securityAgency" },
  retail: { defaultMessage: "Retail", id: "retail" },
  other: { defaultMessage: "Other", id: "other" },
  billing: { defaultMessage: "Billing", id: "billing" },
  shipping: { defaultMessage: "Shipping", id: "shipping" },
  main: { defaultMessage: "Main", id: "main" },
  work: { defaultMessage: "Work", id: "work" },
  home: { defaultMessage: "Home", id: "home" },
  previous: { defaultMessage: "Previous", id: "previous" },
  mobile: { defaultMessage: "Mobile", id: "mobile" },
  fax: { defaultMessage: "Fax", id: "fax" },
  personal: { defaultMessage: "Personal", id: "personal" }
});

/**
 * Inspect component for the Business entity, allows the user to create,
 * edit or delete records if they have permission to do so.
 */
export const Inspect = ({
  theme,
  findBusinessByIdPropQuery,
  saveBusinessPropMutation,
  deleteBusinessPropMutation,
  findBusinessByIdPropQueryName,
  objectWrapperProp,
  mutationPropName,
  moreItems: MoreItems,
  typesOptions,
  inspectTitle,
  link,
  linkType,
  onLinkPress,
  idSelected,
  selectId,
  onImageUploaded,
  data,
  ...restProps
}) => {
  const photoAllowed = data.sessionInformation &&
    hasPermissions(data.sessionInformation.user.permissions, {
      objects: "BusinessPhoto",
      actions: ["create, update"]
    });

  return (
    <MutableInspect
      title={inspectTitle || messages.business}
      entityName="Business"
      query={findBusinessByIdPropQuery || findBusinessByIdQuery}
      queryName={findBusinessByIdPropQueryName || "findBusinessById"}
      mutation={saveBusinessPropMutation || saveBusinessMutation}
      mutationName={mutationPropName || "saveBusiness2"}
      objectWrapper={objectWrapperProp || "business"}
      onQueryCompleted={data => {
        // When selecting another business after selectg an id, the selection must be reset.
        if (selectId && data[findBusinessByIdPropQueryName] && (data[findBusinessByIdPropQueryName].externalId === null || data[findBusinessByIdPropQueryName].externalId)) {
          selectId(data[findBusinessByIdPropQueryName].externalId)
        } else if (selectId && data.findBusinessById && (data.findBusinessById.externalId === null || data.findBusinessById.externalId)) {
          selectId(data.findBusinessById.externalId)
        }
      }}
      defaultValues={{
        status: "active",
        type: link ? "team" : "other",
        contact: {
          type: "business"
        }
      }}
      validationSchema={yupObject().shape({
        type: yupString().oneOf(typesOptions ? typesOptions.map(t => t.id) : [
          messages.medicalPractice,
          messages.securityAgency,
          messages.retail,
          messages.other
        ].map(t => t.id), "requiredFieldError"),
        contact: yupObject().shape({
          companyName: yupString().required(),
          emails: yupArray().of(
            yupObject().shape({
              address: yupString()
                .required()
                .email("invalidEmailFieldError")
            })
          )
        })
      })}
      deleteMutation={deleteBusinessPropMutation || deleteBusinessMutation}
      {...restProps}
    >
      {() => (
        <Container>
          {!theme.hideBusinessInspectPhoto && photoAllowed && (
            <Section>
              {/* Photo */}
              <FormSpy>
                {({ form }) => (
                  <FormCell size="stretch">
                    <FormUploadImageField
                      name="contact.photoId"
                      size={140}
                      border={4}
                      entityName="Contact"
                      alignSelf="center"
                      onUploaded={token => onImageUploaded && onImageUploaded(token, form)}
                    />
                  </FormCell>
                )}
              </FormSpy>
            </Section>
          )}

          <Section>
            {/* Company Name */}
            <FormCell size="stretch">
              <FormTextField
                label={messages.companyName}
                name="contact.companyName"
              />
            </FormCell>

            <FormSpy>
              {({ form: { getFieldState } }) => {
                if (link && getFieldState("type") && getFieldState("type").value === linkType) {
                  return (
                    <FormCell xlSize={100}>
                      <Field name="externalId">
                        {({ input, meta }) => {
                          idSelected && input.onChange(idSelected)
                          return (
                            <Button
                              leftIcon="link"
                              ui={input.value ? "linkedTeam" : "notLinkedTeam"}
                              disabled={false}
                              onPress={() => {
                                if (input.value && (idSelected === null || idSelected !== input.value)) {
                                  selectId && selectId(input.value)
                                }

                                onLinkPress && onLinkPress(true);
                              }}
                            />
                          )
                        }}
                      </Field>
                    </FormCell>
                  )
                }
                return null;
              }}
            </FormSpy>

            <FormCell xlSize={150}>
              <FormSelectValueField
                label={messages.type}
                options={
                  typesOptions || [
                    messages.medicalPractice,
                    messages.securityAgency,
                    messages.retail,
                    messages.other
                  ]
                }
                name="type"
              />
            </FormCell>

            {/* Status */}
            <FormCell xlSize={150}>
              <FormGeneralStatusField />
            </FormCell>
          </Section>

          <Section>
            {/* First Name */}
            <FormCell xlSize="1/2">
              <FormTextField
                label={messages.firstName}
                name="contact.firstName"
              />
            </FormCell>

            {/* Last Name */}
            <FormCell xlSize="1/2">
              <FormTextField label={messages.lastName} name="contact.lastName" />
            </FormCell>
          </Section>

          {!theme.hide2FACheckbox && (
            <FormSpy>
              {({ form: { change } }) => (
                <Section>
                  {/* MFA ENABLED */}
                  <FormCell size="stretch">
                    <FormCheckbox
                      leftLabel={messages.mfaEnabled}
                      name="mfaEnabled"
                      alignSelf="flex-end"
                      backgroundColor="transparent"
                      onPress={checked => {
                        change("mfaEnabledChange", true)
                      }}
                    />
                  </FormCell>
                </Section>
              )}
            </FormSpy>

          )}

          {/* Addresses */}
          <InspectSubtitle title={messages.addresses} />
          <FormToManyField
            name="contact.addresses"
            addValidation={yupObject().shape({
              line1: yupString().required(),
              city: yupString().required(),
              state: yupString().required(),
              zipCode: yupString().required()
            })}
            defaultValues={{ country: "US" }}
            renderNewOnButton
          >
            {({ name, isNew, push, remove, value, index, plus, length }) => (
              <Container
                key={name}
                flexDirection="row"
                paddings="0 10px"
                width="100%"
              >
                <Container margins="10px 0 0">
                  <DeletableItem
                    allowDelete={!isNew}
                    height="60px"
                    confirmMessage={messages.confirmDeleteItem}
                    onConfirm={remove}
                    checkPermissions={{
                      objects: "ContactAddress",
                      actions: ["delete"]
                    }}
                  >
                    <RoundedIcon
                      iconSize={50}
                      color={
                        theme.roundedIconColor ||
                        theme.fieldFontColor ||
                        theme.fontColor
                      }
                      name={isNew ? "map-marker-plus" : "map-marker-alt"}
                    />
                  </DeletableItem>
                </Container>

                <Section style={{ flex: 1 }}>
                  <Container
                    margins="10px 0 0 10px"
                    flex={2}
                    style={{ minWidth: 200 }}
                  >
                    <FormSelectValueField
                      label={messages.type}
                      options={[
                        messages.main,
                        messages.work,
                        messages.home,
                        messages.billing,
                        messages.shipping,
                        messages.previous
                      ]}
                      name={`${name}.tags`}
                    />
                  </Container>
                  <Container
                    margins="10px 0 0 10px"
                    flex={2}
                    style={{ minWidth: 200 }}
                  >
                    <FormTextField
                      label={messages.line1}
                      name={`${name}.line1`}
                      onBlur={push}
                    />
                  </Container>
                  <Container
                    margins="10px 0 0 10px"
                    flex={1.5}
                    style={{ minWidth: 150 }}
                  >
                    <FormTextField
                      label={messages.city}
                      name={`${name}.city`}
                      onBlur={push}
                    />
                  </Container>
                  <Container
                    margins="10px 0 0 10px"
                    flex={1}
                    style={{ minWidth: 100 }}
                  >
                    <FormTextField
                      label={messages.state}
                      name={`${name}.state`}
                      onBlur={push}
                    />
                  </Container>
                  <Container
                    margins="10px 0 0 10px"
                    flex={1}
                    style={{ minWidth: 100 }}
                  >
                    <FormTextField
                      label={messages.zipCode}
                      name={`${name}.zipCode`}
                      onBlur={push}
                    />
                  </Container>

                  {/* New Item Button */}
                  <SecuredComponent
                    checkPermissions={{
                      objects: "ContactAddress",
                      actions: ["create"]
                    }}
                  >
                    {!isNew && index + 1 === length && theme.renderNewOnButton ? (
                      <Button
                        leftIcon="plus"
                        margins="40px 0 0 10px"
                        ui="dangerLink"
                        leftIconWeight={900}
                        onPress={plus}
                      />
                    ) : null}
                  </SecuredComponent>
                </Section>
              </Container>
            )}
          </FormToManyField>

          {/* Phones */}
          <InspectSubtitle title={messages.phones} />
          <FormToManyField name="contact.phones" renderNewOnButton>
            {({ name, isNew, push, remove, value, index, length, plus }) => (
              <Container
                key={name}
                flexDirection="row"
                alignItems="center"
                paddings="10px"
                width="100%"
                style={{ maxWidth: 500 }}
              >
                <DeletableItem
                  allowDelete={!isNew}
                  height="60px"
                  confirmMessage={messages.confirmDeleteItem}
                  onConfirm={remove}
                  checkPermissions={{
                    objects: "ContactPhone",
                    actions: ["delete"]
                  }}
                >
                  <RoundedIcon
                    iconSize={50}
                    color={
                      theme.roundedIconColor ||
                      theme.fieldFontColor ||
                      theme.fontColor
                    }
                    name={isNew ? "plus-circle" : "phone"}
                  />
                </DeletableItem>

                <FormSelectValueField
                  margins="0 0 0 10px"
                  width="120px"
                  placeholder={messages.type}
                  options={[
                    messages.main,
                    messages.work,
                    messages.home,
                    messages.mobile,
                    messages.billing,
                    messages.shipping,
                    messages.fax
                  ]}
                  name={`${name}.tags`}
                />
                <FormTextField
                  flex={1}
                  margins="0 0 0 10px"
                  placeholder={messages.number}
                  name={`${name}.phoneNumber`}
                  onBlur={push}
                />

                {/* New Item Button */}
                <SecuredComponent
                  checkPermissions={{
                    objects: "ContactPhone",
                    actions: ["create"]
                  }}
                >
                  {!isNew && index + 1 === length && theme.renderNewOnButton ? (
                    <Button
                      leftIcon="plus"
                      margins="0 0 0 10px"
                      ui="dangerLink"
                      leftIconWeight={900}
                      onPress={plus}
                    />
                  ) : null}
                </SecuredComponent>
              </Container>
            )}
          </FormToManyField>

          {/* E-mails */}
          <InspectSubtitle title={messages.emails} />
          <FormToManyField
            name="contact.emails"
            addValidation={yupObject().shape({
              address: yupString()
                .required()
                .email()
            })}
            renderNewOnButton
          >
            {({ name, isNew, push, remove, index, length, plus }) => (
              <Container
                key={name}
                flexDirection="row"
                alignItems="center"
                paddings="10px"
                width="100%"
                style={{ maxWidth: 500 }}
              >
                <DeletableItem
                  allowDelete={!isNew}
                  height="60px"
                  confirmMessage={messages.confirmDeleteItem}
                  onConfirm={remove}
                  checkPermissions={{
                    objects: "ContactEmail",
                    actions: ["delete"]
                  }}
                >
                  {/* E-mail or add icon */}
                  <RoundedIcon
                    iconSize={50}
                    color={
                      theme.roundedIconColor ||
                      theme.fieldFontColor ||
                      theme.fontColor
                    }
                    name={isNew ? "plus-circle" : "envelope"}
                  />
                </DeletableItem>

                {/* E-mail address field */}
                <FormEmailField
                  name={`${name}.address`}
                  onBlur={push}
                  margins="0 0 0 10px"
                  flex={1}
                />

                {/* New Item Button */}
                <SecuredComponent
                  checkPermissions={{
                    objects: "ContactEmail",
                    actions: ["create"]
                  }}
                >
                  {!isNew && index + 1 === length && theme.renderNewOnButton ? (
                    <Button
                      leftIcon="plus"
                      margins="0 0 0 10px"
                      ui="dangerLink"
                      leftIconWeight={900}
                      onPress={plus}
                    />
                  ) : null}
                </SecuredComponent>
              </Container>
            )}
          </FormToManyField>

          {/* More items, possibly Categories */}
          {MoreItems && <MoreItems />}
        </Container>
      )}
    </MutableInspect>
  );
};
export default graphql(getSessionInformation)(withTheme(Inspect));
